/**
 * Gallery Terms & Conditions block, authored per UI language.
 *
 * Production widgets refetch partner terms via `useTerms` when the language
 * changes. The screenbook has no API, so this map is the static stand-in:
 * `useGalleryTerms` swaps `session.terms` whenever the Control Dock language
 * changes so Welcome / TermDetail stay in sync (en / es / fr).
 */
import type { ITermsBlock } from "../shared/session/interfaces/session";

export type GalleryTermsLang = "en" | "es" | "fr";

export function galleryTermsLang(code?: string): GalleryTermsLang {
  const base = (code ?? "en").split("-")[0]?.toLowerCase() ?? "en";
  if (base === "es" || base === "fr") return base;
  return "en";
}

const AVAILABLE: GalleryTermsLang[] = ["en", "es", "fr"];

const TERMS_DETAIL_BODY = {
  en: [
    "Sample Terms & Conditions body shown in the screenbook. The index below uses GitHub-style anchor links that scroll within the document.",
    "## Index",
    "- [1. Scope](#1-scope)\n- [2. Privacy](#2-privacy)\n- [3. Fees and Exchange Rates](#3-fees-and-exchange-rates)\n- [4. Cancellations and Refunds](#4-cancellations-and-refunds)\n- [5. Contact](#5-contact)",
    "## 1. Scope",
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
    "## 2. Privacy",
    "Praesent commodo, sapien nec lacinia tincidunt, dui lorem bibendum mi, in facilisis magna nulla vitae lectus. Curabitur blandit tempus porttitor. Donec ullamcorper nulla non metus auctor fringilla.",
    "Vestibulum id ligula porta felis euismod semper. Aenean lacinia bibendum nulla sed consectetur. Nulla vitae elit libero, a pharetra augue.",
    "## 3. Fees and Exchange Rates",
    "Maecenas faucibus mollis interdum. Cras mattis consectetur purus sit amet fermentum. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.",
    "### 3.1 Disclosure",
    "Etiam porta sem malesuada magna mollis euismod. Donec id elit non mi porta gravida at eget metus.",
    "## 4. Cancellations and Refunds",
    "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.",
    "## 5. Contact",
    "Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Back to [Index](#index).",
  ].join("\n\n"),
  es: [
    "Cuerpo de ejemplo de Términos y Condiciones en el screenbook. El índice usa anclas estilo GitHub que hacen scroll dentro del documento.",
    "## Índice",
    "- [1. Alcance](#1-alcance)\n- [2. Privacidad](#2-privacidad)\n- [3. Comisiones y tipos de cambio](#3-comisiones-y-tipos-de-cambio)\n- [4. Cancelaciones y reembolsos](#4-cancelaciones-y-reembolsos)\n- [5. Contacto](#5-contacto)",
    "## 1. Alcance",
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
    "## 2. Privacidad",
    "Praesent commodo, sapien nec lacinia tincidunt, dui lorem bibendum mi, in facilisis magna nulla vitae lectus. Curabitur blandit tempus porttitor. Donec ullamcorper nulla non metus auctor fringilla.",
    "Vestibulum id ligula porta felis euismod semper. Aenean lacinia bibendum nulla sed consectetur. Nulla vitae elit libero, a pharetra augue.",
    "## 3. Comisiones y tipos de cambio",
    "Maecenas faucibus mollis interdum. Cras mattis consectetur purus sit amet fermentum. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.",
    "### 3.1 Divulgación",
    "Etiam porta sem malesuada magna mollis euismod. Donec id elit non mi porta gravida at eget metus.",
    "## 4. Cancelaciones y reembolsos",
    "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.",
    "## 5. Contacto",
    "Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Volver al [Índice](#índice).",
  ].join("\n\n"),
  fr: [
    "Corps d'exemple des Conditions générales dans le screenbook. L'index utilise des ancres de style GitHub qui défilent dans le document.",
    "## Index",
    "- [1. Portée](#1-portée)\n- [2. Confidentialité](#2-confidentialité)\n- [3. Frais et taux de change](#3-frais-et-taux-de-change)\n- [4. Annulations et remboursements](#4-annulations-et-remboursements)\n- [5. Contact](#5-contact)",
    "## 1. Portée",
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
    "## 2. Confidentialité",
    "Praesent commodo, sapien nec lacinia tincidunt, dui lorem bibendum mi, in facilisis magna nulla vitae lectus. Curabitur blandit tempus porttitor. Donec ullamcorper nulla non metus auctor fringilla.",
    "Vestibulum id ligula porta felis euismod semper. Aenean lacinia bibendum nulla sed consectetur. Nulla vitae elit libero, a pharetra augue.",
    "## 3. Frais et taux de change",
    "Maecenas faucibus mollis interdum. Cras mattis consectetur purus sit amet fermentum. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.",
    "### 3.1 Divulgation",
    "Etiam porta sem malesuada magna mollis euismod. Donec id elit non mi porta gravida at eget metus.",
    "## 4. Annulations et remboursements",
    "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.",
    "## 5. Contact",
    "Fusce dapibus, tellus ac cursus commodo, tortor mauris condimentum nibh, ut fermentum massa justo sit amet risus. Retour à l'[Index](#index).",
  ].join("\n\n"),
} as const;

const TERMS_BY_LANG: Record<GalleryTermsLang, ITermsBlock> = {
  en: {
    version: "gallery-v1",
    language: "en",
    availableLanguages: AVAILABLE,
    entries: [
      {
        id: "esign",
        required: true,
        introMarkdown:
          "I acknowledge that I have read and agree to the terms of the [E-sign Consent](#).",
        detailTitle: "E-sign Consent",
        detailMarkdown:
          "# E-sign Consent\n\nThis is sample E-sign consent copy rendered in the screenbook. In production the partner-configured markdown is delivered with the session.\n\n## Use of electronic records\n\nLorem ipsum dolor sit amet, consectetur adipiscing elit. You agree to receive disclosures electronically.",
      },
      {
        id: "terms",
        required: true,
        introMarkdown:
          "I acknowledge that I have read and agree to the **Terms & Conditions**, which include the End User Agreement and Privacy Policy.",
        detailTitle: "Terms & Conditions",
        detailMarkdown: `# Terms & Conditions\n\n${TERMS_DETAIL_BODY.en}`,
      },
    ],
  },
  es: {
    version: "gallery-v1",
    language: "es",
    availableLanguages: AVAILABLE,
    entries: [
      {
        id: "esign",
        required: true,
        introMarkdown:
          "Reconozco que he leído y acepto los términos del [Consentimiento Electrónico](#).",
        detailTitle: "Consentimiento Electrónico",
        detailMarkdown:
          "# Consentimiento Electrónico\n\nEste es un texto de ejemplo del consentimiento electrónico en el screenbook. En producción el markdown configurado por el partner llega con la sesión.\n\n## Uso de registros electrónicos\n\nLorem ipsum dolor sit amet, consectetur adipiscing elit. Aceptas recibir divulgaciones por medios electrónicos.",
      },
      {
        id: "terms",
        required: true,
        introMarkdown:
          "Reconozco que he leído y acepto los **Términos y Condiciones**, que incluyen el Acuerdo de Usuario Final y la Política de Privacidad.",
        detailTitle: "Términos y Condiciones",
        detailMarkdown: `# Términos y Condiciones\n\n${TERMS_DETAIL_BODY.es}`,
      },
    ],
  },
  fr: {
    version: "gallery-v1",
    language: "fr",
    availableLanguages: AVAILABLE,
    entries: [
      {
        id: "esign",
        required: true,
        introMarkdown:
          "Je reconnais avoir lu et j'accepte les conditions du [Consentement à la signature électronique](#).",
        detailTitle: "Consentement à la signature électronique",
        detailMarkdown:
          "# Consentement à la signature électronique\n\nCeci est un texte d'exemple du consentement électronique dans le screenbook. En production, le markdown configuré par le partenaire est livré avec la session.\n\n## Utilisation des enregistrements électroniques\n\nLorem ipsum dolor sit amet, consectetur adipiscing elit. Vous acceptez de recevoir des divulgations par voie électronique.",
      },
      {
        id: "terms",
        required: true,
        introMarkdown:
          "Je reconnais avoir lu et j'accepte les **Conditions générales**, qui incluent l'accord d'utilisateur final et la politique de confidentialité.",
        detailTitle: "Conditions générales",
        detailMarkdown: `# Conditions générales\n\n${TERMS_DETAIL_BODY.fr}`,
      },
    ],
  },
};

/** Localized gallery terms for the active UI language (English fallback). */
export function getMockTerms(lang?: string): ITermsBlock {
  return TERMS_BY_LANG[galleryTermsLang(lang)];
}
